import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useDispatch } from 'react-redux';
import { registerScreenv2 } from 'src/redux/actions/Screen';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export const PairingScreenDataContext = React.createContext()

export default function QuickScreenPairingStepperControl({
    step1,
    step2,
    step3,
    step4,
    resetFormAndClose,
    syncScreen
}) { 
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const pairScreenBtn = React.useRef(null)
  const theme = useTheme()

  // Custom hook: determine current MUI breakpoint: xs | sm | md | lg | xl
  const useCurrentBreakpoint = () => {
    const upSm = useMediaQuery(theme.breakpoints.up('sm'))
    const upMd = useMediaQuery(theme.breakpoints.up('md'))
    const upLg = useMediaQuery(theme.breakpoints.up('lg'))
    const upXl = useMediaQuery(theme.breakpoints.up('xl'))
    if (upXl) return 'xl'
    if (upLg) return 'lg'
    if (upMd) return 'md'
    if (upSm) return 'sm'
    return 'xs'
  }
  const currentBreakpoint = useCurrentBreakpoint()

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [isLoading, setIsLoading] = React.useState(false)
  const [pairingDetails, setPairingDetails] = React.useState({
    code: '',
    name: '',
    asset: '',
    location: '',
    isFullLocation: 'false',
    assetType: '',
    locationDetails: {
      mostDetailedLevel: '',
      lat: '',
      lng: '',
      formatted_address: '',
      short_address: ''
    }
  })
  const [openConfirmContinueDialog, setOpenConfirmContinueDialog] = React.useState(false)
  const steps = [t("components.pairScreenStepper.step1"), t("components.pairScreenStepper.step2"), t("components.pairScreenStepper.step3"), t("components.pairScreenStepper.step4")];

  const stepsComponents = [step1, step2, step3, step4]
    
  const isStepOptional = (step) => {
    return false;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  React.useEffect(() => {
    console.log(pairingDetails)
  }, [pairingDetails])

  const handleNext = (e) => {
    e.preventDefault()
    switch (activeStep)  {
        case 0:
            setPairingDetails({...pairingDetails, code: e.target[0].value})
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            break
        case 1:
            setPairingDetails({...pairingDetails, name: e.target[0].value})
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            break
        case 2:
            setPairingDetails({...pairingDetails, asset: e.target[0].dataset.assetid, assetType: e.target[0].dataset.assettype})
            if(e.target[0].dataset.assetid) {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }else { 
                toast.error(t("views.redesignScreens.assetIsRequired"))
            }
            break
        case 3:
            setPairingDetails({...pairingDetails, location: e.target[0].dataset.location, isFullLocation: e.target[0].dataset.isfull,
              locationDetails: {
                mostDetailedLevel: e.target[0].dataset.mostdetailedlevel,
                lat: e.target[0].dataset.lat,
                lng: e.target[0].dataset.lng,
                formatted_address: e.target[0].dataset.formattedaddress,
                short_address: e.target[0].dataset.shortaddress
              }
            })
            if(e.target[0].dataset.isfull == 'false') {
              setOpenConfirmContinueDialog(true)
            }else{
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
            setTimeout(() => {
              pairScreenBtn.current.click()
            }, 100)
            break
        case 4:
          pairScreen()
    }
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setSkipped(newSkipped);
  };

  const pairScreen = async () => {
    setIsLoading(true)
    // console.log(pairingDetails.code, {
    //   deviceName: pairingDetails.name,
    //   assetId: pairingDetails.asset,
    //   assetType: pairingDetails.assetType,
    //   locationDetails: {...pairingDetails.locationDetails, fullLocation: pairingDetails.isFullLocation == 'true' ? true : false},
    //   location: pairingDetails.location,
    //   lat: pairingDetails.locationDetails.lat,
    //   lng: pairingDetails.locationDetails.lng,
    // })

    const isOk = Object.values(pairingDetails).filter(v => !v)

    if(isOk.length == 0) {
      const result = await dispatch(registerScreenv2(pairingDetails.code, {
        deviceName: pairingDetails.name,
        assetId: pairingDetails.asset,
        assetType: pairingDetails.assetType,
        locationDetails: {...pairingDetails.locationDetails, fullLocation: pairingDetails.isFullLocation == 'true' ? true : false},
        location: pairingDetails.location,
        lat: pairingDetails.locationDetails.lat,
        lng: pairingDetails.locationDetails.lng,
      }))
      console.log(result.mac)
      syncScreen(result.mac)
      // onSyncScreen(result.mac);
      resetFormAndClose()
    }else {
      toast.error(t("views.redesignScreens.formIncomplete"))
      setIsLoading(false)
    }
  }

  const handleBack = (e) => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <>
        <Dialog open={openConfirmContinueDialog} onClose={() => setOpenConfirmContinueDialog(false)}>
            <DialogTitle sx={{
              mt: 2
            }} textAlign="center">{t("views.redesignScreens.continueAreYouSure")}</DialogTitle>
            <DialogContent>
                <DialogContentText textAlign="center">
                    {t("views.redesignScreens.locationNotAccurate")}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3
            }}>
                <Button variant='outlined' size={"small"} color="error" onClick={() => setOpenConfirmContinueDialog(false)}>{t("views.redesignScreens.noCancel")}</Button>
                <Button variant='outlined' size={"small"} onClick={pairScreen}>{t("views.redesignScreens.yesContinue")}</Button>
            </DialogActions>
        </Dialog>
        <Box sx={{ width: '100%', px:4, py:4 }}>
        <Stepper activeStep={activeStep} orientation={(currentBreakpoint === 'xs' || currentBreakpoint === 'sm') ? 'vertical' : 'horizontal'} sx={{
          mb: 3
        }}>
            {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            if (isStepOptional(index)) {
                labelProps.optional = (
                <Typography variant="caption">Optional</Typography>
                );
            }
            if (isStepSkipped(index)) {
                stepProps.completed = false;
            }
            return (
                <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
            );
            })}
        </Stepper>
        {activeStep === steps.length ? (
            <React.Fragment>
            <Typography sx={{ mt: 2, mb: 1 }}>
                All steps completed - pair the screen
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Box sx={{ flex: '1 1 auto' }} />
                {!isLoading ? (
                    <Button onClick={pairScreen} ref={pairScreenBtn}>Pair</Button>
                ) : (
                    <CircularProgress size="30px" />
                )}
            </Box>
            </React.Fragment>
        ) : (
            <form onSubmit={handleNext} onReset={handleBack}>
                <PairingScreenDataContext.Provider value={pairingDetails} >
                    {stepsComponents[activeStep]}
                </PairingScreenDataContext.Provider>
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button
                color="inherit"
                disabled={activeStep === 0}
                type='reset'
                sx={{ mr: 1 }}
                >
                Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {isStepOptional(activeStep) && (
                <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                    Skip
                </Button>
                )}
                {!isLoading ? (
                    <Button type='submit'>
                    {activeStep === steps.length - 1 ? 'Pair' : 'Next'}
                    </Button>
                ) : (
                    <CircularProgress size="30px" />
                )}
            </Box>
            </form>
        )}
        </Box>
    </>
  );
}
